import { HttpMethods } from "@kvix/shared";
import { makeStyles } from "@material-ui/core";
import {
  endOfMonth,
  format,
  getDate,
  getDay,
  getDaysInMonth,
  isToday,
  parseJSON,
  startOfMonth,
} from "date-fns";
import { enGB } from "date-fns/locale";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { animated, useSpring } from "react-spring";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",

    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap-reverse",
    },

    [theme.breakpoints.down("sm")]: {
      width: "100vw",
      position: "relative",
      marginLeft: "-50vw",
      left: "50%",
      display: "flex",
      justifyContent: "center",
      padding: "0 32px 32px",
      boxShadow: "none",
    },
  },
  left: { padding: 20 },
  right: {
    padding: "20px 10px 20px 40px",
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexGrow: 1,
    flexShrink: 0,
    flexDirection: "column",
    overflow: "hidden",

    [theme.breakpoints.down("xs")]: {
      height: 90,
      padding: "10px 0px",
      flexBasis: "100%",
      flexDirection: "row",
    },
    "&>div": {
      whiteSpace: "nowrap",
      flex: 1,
      transform: "translateX(200%)",
      animation: "$slideIn 0.1s forwards",

      [theme.breakpoints.down("xs")]: {
        textAlign: "center",
      },
    },
    "&>div:nth-of-type(1)": {
      paddingTop: 2,

      [theme.breakpoints.down("xs")]: {
        display: "none",
      },
    },
    "&>div>div:nth-of-type(2)": {
      textAlign: "left",
      fontSize: 30,
      fontWeight: 700,
      marginTop: 10,

      [theme.breakpoints.down("xs")]: {
        textAlign: "center",
      },
    },
  },
  table: {
    tableLayout: "fixed",
    borderSpacing: 0,
    "& th": { paddingBottom: 13 },
    "& td": {
      fontWeight: 500,
      padding: 0,
      height: 60,
      width: 60,
      textAlign: "center",
      backgroundClip: "content-box",

      [theme.breakpoints.down(1040)]: {
        height: 55,
        width: 55,
      },

      [theme.breakpoints.down(960)]: {
        height: 80,
        width: 80,
      },

      [theme.breakpoints.down(850)]: {
        height: 70,
        width: 70,
      },

      [theme.breakpoints.down(740)]: {
        height: 60,
        width: 60,
      },

      [theme.breakpoints.down(680)]: {
        height: 40,
        width: 40,
      },

      [theme.breakpoints.down(390)]: {
        height: 45,
        width: 45,
      },

      [theme.breakpoints.down(390)]: {
        height: 45,
        width: 45,
      },

      [theme.breakpoints.down(350)]: {
        height: 40,
        width: 40,
      },
    },
  },
  cellStreak: {
    borderRadius: 60,
    backgroundColor: "#6BD303",
    color: theme.palette.common.white,
    border: `12px solid ${theme.palette.background.default}`,

    [theme.breakpoints.down(960)]: {
      border: `18px solid ${theme.palette.background.default}`,
    },

    [theme.breakpoints.down(850)]: {
      border: `16px solid ${theme.palette.background.default}`,
    },

    [theme.breakpoints.down(740)]: {
      border: `12px solid ${theme.palette.background.default}`,
    },

    [theme.breakpoints.down(680)]: {
      border: `2px solid ${theme.palette.background.default}`,
    },

    [theme.breakpoints.down(390)]: {
      border: `5px solid ${theme.palette.background.default}`,
    },
  },
  cellStreakStart: {
    borderRight: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  cellStreakEnd: {
    borderLeft: 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  cellStreakMiddle: {
    borderRadius: 0,
    borderLeft: 0,
    borderRight: 0,
  },
  cellStreakAnimate: {
    transition:
      "background-color 500ms ease-out 500ms, color 500ms ease-out 500ms",
  },
  "@keyframes slideIn": {
    "100%": { transform: "translateX(0%)" },
  },
}));

const Counter: React.FC<{ from: number; to: number; delay: number }> = (
  props
) => {
  const { val } = useSpring({
    config: { duration: 500 },
    delay: props.delay,
    from: { val: props.from },
    to: { val: props.to },
  });
  const { x } = useSpring({
    config: { duration: 700 },
    delay: props.delay,
    from: { x: -1 },
    to: { x: 1 },
  });

  return props.from < props.to ? (
    <animated.div
      style={{
        display: "inline-block",
        transform: x.interpolate((x) => `scale(${-Math.pow(x, 2) + 2})`),
      }}
    >
      {val.interpolate((val) => Math.floor(val).toString().padStart(2, "0"))}
    </animated.div>
  ) : (
    <div style={{ display: "inline-block" }}>
      {props.to.toString().padStart(2, "0")}
    </div>
  );
};

export const MonthActivity: React.FC<{ date: Date; alwaysAnimate: boolean }> = (
  props
) => {
  const { t, ready } = useTranslation(["account", "general"]);
  const classes = useStyles(props);
  const date = props.date;
  const alwaysAnimate = props.alwaysAnimate;
  const locale = enGB;
  const weekOrder = [1, 2, 3, 4, 5, 6, 0];
  const weekdays = weekOrder.map((i) => locale.localize.day(i).toLowerCase());
  const firstDay = getDay(startOfMonth(date));
  const [stats, setStats] = useState<{
    streak: number[];
    bestStreak: number | null;
    traningMinutes: number | null;
    traningClasses: number | null;
    animate: {
      streak: number[];
      bestStreak: number;
      traningMinutes: number;
      traningClasses: number;
    };
  }>({
    streak: [],
    bestStreak: null,
    traningMinutes: null,
    traningClasses: null,
    animate: {
      streak: [],
      bestStreak: 0,
      traningMinutes: 0,
      traningClasses: 0,
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      const today = {
        streak: [],
        traningSeconds: 0,
        traningDays: {},
        traningClasses: {},
      };
      let traningSeconds = 0;
      let traningDays = {};
      let traningClasses = {};

      const response = await fetch(
        `/api/statistic/me?from=${format(
          startOfMonth(date),
          "yyyy-MM-dd"
        )}&to=${format(endOfMonth(date), "yyyy-MM-dd")}`,
        {
          method: HttpMethods.GET,
        }
      );

      const objects: {
        liveSessionUserStatistic_start: string;
        liveSessionUserStatistic_secondsWatched: number;
        liveSession_id: number;
      }[] = await response.json();

      for (const value of objects instanceof Array ? objects : []) {
        const startDate = parseJSON(value.liveSessionUserStatistic_start);

        if (
          value.liveSessionUserStatistic_secondsWatched > 0 &&
          startOfMonth(date) <= startDate &&
          endOfMonth(date) >= startDate
        ) {
          if (isToday(startDate)) {
            today.traningSeconds +=
              value.liveSessionUserStatistic_secondsWatched;

            today.traningDays[getDate(startDate)] = today.traningDays[
              getDate(startDate)
            ]
              ? today.traningDays[getDate(startDate)] +
                value.liveSessionUserStatistic_secondsWatched
              : value.liveSessionUserStatistic_secondsWatched;

            today.traningClasses[value.liveSession_id] = today.traningClasses[
              value.liveSession_id
            ]
              ? today.traningClasses[value.liveSession_id] +
                value.liveSessionUserStatistic_secondsWatched
              : value.liveSessionUserStatistic_secondsWatched;
          } else {
            traningSeconds += value.liveSessionUserStatistic_secondsWatched;

            traningDays[getDate(startDate)] = traningDays[getDate(startDate)]
              ? traningDays[getDate(startDate)] +
                value.liveSessionUserStatistic_secondsWatched
              : value.liveSessionUserStatistic_secondsWatched;

            traningClasses[value.liveSession_id] = traningClasses[
              value.liveSession_id
            ]
              ? traningClasses[value.liveSession_id] +
                value.liveSessionUserStatistic_secondsWatched
              : value.liveSessionUserStatistic_secondsWatched;
          }
        }
      }

      // Only count sessions >= 300 seconds
      const filter = (object: any) => {
        const newObj = {};

        Object.entries(object).forEach(([key, value]) => {
          if (value as number >= 300) {
            newObj[key] = value;
          }
        });

        return newObj;
      };

      traningDays = filter(traningDays);
      today.traningDays = filter(today.traningDays);
      traningClasses = filter(traningClasses);
      today.traningClasses = filter(today.traningClasses);

      let prev = -1;
      const uniqueTraningDays = [
        ...Object.keys(traningDays),
        ...Object.keys(today.traningDays),
      ]
        .map((v) => Number(v))
        .sort((a, b) => a - b);
      const chunks = [];

      uniqueTraningDays.forEach((current) => {
        if (current - prev !== 1) {
          chunks.push([]);
        }
        chunks[chunks.length - 1] && chunks[chunks.length - 1].push(current);
        prev = current;
        if (current === getDate(new Date())) {
          today.streak = chunks[chunks.length - 1];
          chunks.splice(chunks.length - 1);
        }
      });
      chunks.sort((a, b) => b.length - a.length);

      const numTraningClasses = Object.keys(traningClasses).length;
      const numTraningClassesToday = Object.keys(today.traningClasses).length;

      alwaysAnimate
        ? setStats({
            streak: uniqueTraningDays,
            bestStreak: 0,
            traningMinutes: 0,
            traningClasses: 0,
            animate: {
              streak: uniqueTraningDays,
              traningClasses: numTraningClasses + numTraningClassesToday,
              traningMinutes:
                Math.floor(traningSeconds / 60) +
                Math.floor(today.traningSeconds / 60),
              bestStreak:
                chunks.length > 0 && chunks[0].length > today.streak.length
                  ? chunks[0].length
                  : today.streak.length,
            },
          })
        : setStats({
            streak: uniqueTraningDays,
            bestStreak: chunks.length > 0 ? chunks[0].length : 0,
            traningMinutes: Math.floor(traningSeconds / 60),
            traningClasses: numTraningClasses,
            animate: {
              streak: today.streak,
              traningClasses: numTraningClassesToday,
              traningMinutes: Math.floor(today.traningSeconds / 60),
              bestStreak: today.streak.length,
            },
          });
    };

    fetchData();
  }, [alwaysAnimate, date]);

  const tbody = () => {
    const output = [];
    let cells = [];

    for (let i = 0; i < 42; i++) {
      let dayIndex = i + 1 - weekOrder.indexOf(firstDay);
      let classNames = "";

      if (stats.streak.indexOf(dayIndex) !== -1) {
        classNames = classes.cellStreak;

        if (
          stats.streak.indexOf(dayIndex + 1) !== -1 &&
          stats.streak.indexOf(dayIndex - 1) === -1
        ) {
          classNames += ` ${classes.cellStreakStart}`;
        } else if (
          stats.streak.indexOf(dayIndex - 1) !== -1 &&
          stats.streak.indexOf(dayIndex + 1) === -1
        ) {
          classNames += ` ${classes.cellStreakEnd}`;
        } else if (
          stats.streak.indexOf(dayIndex - 1) !== -1 &&
          stats.streak.indexOf(dayIndex + 1) !== -1
        ) {
          classNames += ` ${classes.cellStreakMiddle}`;
        }

        if (stats.animate.streak.indexOf(dayIndex) !== -1) {
          classNames += ` ${classes.cellStreakAnimate}`;
        }
      }

      cells.push(
        <td key={i} className={classNames}>
          {dayIndex > 0 && dayIndex <= getDaysInMonth(date) ? dayIndex : ""}
        </td>
      );

      if ((i + 1) % 7 === 0) {
        output.push(<tr key={Math.floor((i + 1) / 7)}>{cells}</tr>);
        cells = [];
      }
    }

    return <tbody>{output}</tbody>;
  };

  if (!ready) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.left}>
        <table className={classes.table}>
          <thead>
            <tr>
              {weekdays.map((day) => (
                <th key={day}>{t(`general:weekDaysShort.${day}`)}</th>
              ))}
            </tr>
          </thead>
          {tbody()}
        </table>
      </div>
      <div className={classes.right}>
        <div>{t("account:dashboard.monthActivity.overview")}</div>
        {stats.traningMinutes !== null && (
          <>
            <div>
              <div>
                {t("account:dashboard.monthActivity.class", {
                  count: stats.traningClasses + stats.animate.traningClasses,
                })}
              </div>
              <div>
                <Counter
                  from={stats.traningClasses}
                  to={stats.traningClasses + stats.animate.traningClasses}
                  delay={500}
                />{" "}
                👏
              </div>
            </div>
            <div>
              <div>
                {t("account:dashboard.monthActivity.minute", {
                  count: stats.traningMinutes + stats.animate.traningMinutes,
                })}
              </div>
              <div>
                <Counter
                  from={stats.traningMinutes}
                  to={stats.traningMinutes + stats.animate.traningMinutes}
                  delay={1000}
                />{" "}
                💪
              </div>
            </div>
            <div>
              <div>
                {t("account:dashboard.monthActivity.bestStreak", {
                  count: stats.bestStreak + stats.animate.bestStreak,
                })}
              </div>
              <div>
                <Counter
                  from={stats.bestStreak}
                  to={
                    stats.animate.bestStreak > stats.bestStreak
                      ? stats.animate.bestStreak
                      : stats.bestStreak
                  }
                  delay={1500}
                />{" "}
                🔥
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
